import { ORPCError } from '@orpc/client';
import { GRAPH_INLINE_ATTACHMENT_MAX_BYTES } from '@erp/shared-types';
import { base64DecodedBytes } from '@erp/shared-utils';

/** A file sent with the email as raw base64 (e.g. a generated workbook). */
export interface ComposeReplyAttachmentInput {
  name: string;
  contentType: string;
  /** Raw file bytes, base64-encoded. */
  contentBytes: string;
  /**
   * Stored-document id for the same file, when it exists — enables the
   * Office Online rendering in the attachment preview.
   */
  documentId?: string;
}

/**
 * A stored document attached by reference (forwarding an inbound email's
 * attachments) — resolved server-side, no base64 in the browser.
 */
export interface AttachmentDocumentRef {
  documentId: string;
  name: string;
  contentType: string;
  sizeBytes: number;
}

/** Human-readable byte count. */
export function formatByteSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Combined bytes of every attachment that will ride on the message. */
export function attachmentTotalBytes(
  files: ReadonlyArray<ComposeReplyAttachmentInput>,
  refs: ReadonlyArray<AttachmentDocumentRef>,
): number {
  return (
    files.reduce(
      (sum, file) => sum + base64DecodedBytes(file.contentBytes),
      0,
    ) + refs.reduce((sum, ref) => sum + ref.sizeBytes, 0)
  );
}

export interface AttachmentBudget {
  totalBytes: number;
  limitBytes: number;
  /** Bytes that must go before the message fits (0 when under the limit). */
  overBy: number;
  isOver: boolean;
}

/** The inline Graph budget the server enforces, measured against a total. */
export function attachmentBudget(
  totalBytes: number,
  limitBytes: number = GRAPH_INLINE_ATTACHMENT_MAX_BYTES,
): AttachmentBudget {
  const overBy = Math.max(0, totalBytes - limitBytes);
  return { totalBytes, limitBytes, overBy, isOver: overBy > 0 };
}

/** Removes by position: seeded files carry no id and names may repeat. */
export function removeFileAt(
  files: ReadonlyArray<ComposeReplyAttachmentInput>,
  index: number,
): ComposeReplyAttachmentInput[] {
  return files.filter((_, i) => i !== index);
}

export function removeRef(
  refs: ReadonlyArray<AttachmentDocumentRef>,
  documentId: string,
): AttachmentDocumentRef[] {
  return refs.filter((ref) => ref.documentId !== documentId);
}

/**
 * The toast text for a failed send. Server-side `BaseError`s arrive as
 * BAD_REQUEST / FORBIDDEN / NOT_FOUND with a readable message that passes
 * through; a contract (zod) rejection arrives as INPUT_VALIDATION_FAILED with
 * a JSON blob for a message, which the panel's own checks should make
 * unreachable — fall back to something a person can act on.
 */
export function composeReplySendErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (error instanceof ORPCError && error.code === 'INPUT_VALIDATION_FAILED') {
    return fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}
